import { Component, inject, OnDestroy, OnInit, output } from '@angular/core';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { Global360ViewContext, ServiceTypes } from '../../../shared/contexts/global-360-view.context';
import {
  CiscoHelpdeskOptions,
  EmailUserOperationLevel,
  Global360View,
  ServiceProvisioningLevel,
  TileConfig,
} from '../../../shared/models/generated/ziroModels';
import { HelpdeskOptionsContext } from '../../shared/contexts/helpdesk-options.context';
import { Subscription } from 'rxjs';
import { CurrentClusterContext } from '../../../shared/contexts/current-cluster.context';
import { CiscoTileConfigContext } from '../../shared/contexts/cisco-tile-config.context';

@Component({
  selector: 'ziro-operations-menu',
  templateUrl: './operations-menu.component.html',
  styleUrls: ['./operations-menu.component.scss'],
  standalone: false,
})
export class OperationsMenuComponent implements OnInit, OnDestroy {
  private helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private global360ViewContext = inject(Global360ViewContext);
  private _currentClusterContext = inject(CurrentClusterContext);
  private _tileConfigContext = inject(CiscoTileConfigContext);

  readonly reassignClicked = output<void>();
  readonly makePublicClicked = output<void>();
  readonly pinResetClicked = output<void>();
  readonly changeSiteClicked = output<void>();
  readonly sendEmailClicked = output<EmailUserOperationLevel>();

  ziroIcons = ZiroIcons;
  global360View: Global360View;
  helpdeskOptions: CiscoHelpdeskOptions;
  isDeskphoneEnabledOnAnyClusters: boolean;

  private _allCurrentServerIds: number[];

  private _subs = new Subscription();

  ngOnInit(): void {
    const global360ViewSub = this.global360ViewContext.state$.subscribe((state: Global360View) => {
      this.global360View = state;
    });
    this._subs.add(global360ViewSub);

    const helpdeskOptionsSub = this.helpdeskOptionsContext.state$.subscribe((helpdeskOptions) => {
      if (this.helpdeskOptionsContext.isCiscoHelpdeskOptions(helpdeskOptions)) {
        this.helpdeskOptions = helpdeskOptions;
      }
    });
    this._subs.add(helpdeskOptionsSub);

    const currentClusterSub = this._currentClusterContext.state$.subscribe((currentCluster) => {
      this._allCurrentServerIds = this._currentClusterContext.getCurrentClusterServerIds(currentCluster);
    });
    this._subs.add(currentClusterSub);

    const tileConfigContextSub = this._tileConfigContext.state$.subscribe((tileConfig: TileConfig) => {
      this.isDeskphoneEnabledOnAnyClusters = tileConfig.deskphone !== ServiceProvisioningLevel.HIDE;
    });
    this._subs.add(tileConfigContextSub);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  onReassignClick(): void {
    if (this.canReassign()) {
      this.reassignClicked.emit();
    }
  }

  onMakePublicClick(): void {
    if (this.canMakePublic()) {
      this.makePublicClicked.emit();
    }
  }

  onPinResetClick(): void {
    if (this.hasPinToReset()) {
      this.pinResetClicked.emit();
    }
  }

  onChangeSiteClick(): void {
    if (this.hasDevice()) {
      this.changeSiteClicked.emit();
    }
  }

  onSendEmailClick(): void {
    this.sendEmailClicked.emit(this.helpdeskOptions.sendEmailUserOperation);
  }

  canReassign(): boolean {
    return this.hasAnyCiscoServiceInstances();
  }

  getReassignToolTip(): string {
    if (!this.canReassign()) {
      return 'tkey;operations.menu.reassign.disabled_tooltip';
    }
  }

  canMakePublic(): boolean {
    return this.hasDeskphone();
  }

  getMakePublicToolTip(): string {
    if (!this.canMakePublic()) {
      return 'tkey;operations.menu.make_public.disabled_tooltip';
    }
  }

  hasPinToReset(): boolean {
    return this.hasExtensionMobility() || this.hasVoicemail();
  }

  getPinResetTooltip(): string {
    if (!this.hasPinToReset()) {
      return 'tkey;pages.pinreset.nopin.message';
    }
  }

  isChangeSiteEnabled(): boolean {
    return this.helpdeskOptions?.changeSiteEnabled;
  }

  isSendEmailEnabled(): boolean {
    return this.helpdeskOptions?.sendEmailUserOperation !== EmailUserOperationLevel.HIDE;
  }

  getChangeSiteToolTip(): string {
    if (!this.hasDevice()) {
      return 'tkey;operations.menu.change_site.disabled_tooltip';
    }
  }

  hasDevice(): boolean {
    return this.global360ViewContext.hasAnyDeviceInstances(this.global360View, this._allCurrentServerIds);
  }

  hasAnyCiscoServiceInstances(): boolean {
    return this.global360ViewContext.hasAnyServiceInstances(this.global360View, this._allCurrentServerIds);
  }

  hasDeskphone(): boolean {
    return this.global360ViewContext.hasInstanceOf(
      ServiceTypes.DESKPHONE,
      this.global360View,
      this._allCurrentServerIds,
    );
  }

  hasVoicemail(): boolean {
    return this.global360ViewContext.hasInstanceOf(
      ServiceTypes.VOICEMAIL,
      this.global360View,
      this._allCurrentServerIds,
    );
  }

  hasExtensionMobility(): boolean {
    return this.global360ViewContext.hasInstanceOf(
      ServiceTypes.EXTENSION_MOBILITY,
      this.global360View,
      this._allCurrentServerIds,
    );
  }
}
